// Cobros sin confirmar por crédito: su llave vive 10 minutos y se borra al cambiar de sesión.
import type { SellerPaymentOptions } from '@/api/services/loan-credit';
import { useAuthStore } from '@/stores/authStore';
import { chargeAmountOf, chargeRequestOf, type ChargeDraft } from './chargeFlow';

/** Lo mismo que vive la marca de tienda en loan-credit: después la llave ya no ampara el cobro. */
export const PENDING_CHARGE_TTL_MS = 10 * 60_000;

/** Un cobro mandado sin respuesta: su llave, lo que se mandó y cuándo. */
export interface PendingCharge {
    key: string;
    signature: string;
    options: SellerPaymentOptions;
    draft: ChargeDraft;
    amountCents: number;
    installmentNumbers: number[];
    createdAt: number;
}

/** Cuotas que cubre el cobro según las opciones; solo «su cuota» las nombra. */
function installmentNumbersOf(options: SellerPaymentOptions, draft: ChargeDraft): number[] {
    return draft.kind.type === 'NEXT_INSTALLMENT'
        ? (options.nextInstallment.installmentNumbers ?? [])
        : [];
}

/** Huella del cuerpo sin la llave más las cuotas: la llave solo se reusa con la misma. */
export function chargeSignatureOf(options: SellerPaymentOptions, draft: ChargeDraft): string {
    const { idempotencyKey: _key, ...body } = chargeRequestOf(options, draft, '');
    return JSON.stringify({ body, installments: installmentNumbersOf(options, draft) });
}

export function newPendingCharge(
    key: string,
    options: SellerPaymentOptions,
    draft: ChargeDraft,
    now: number = Date.now(),
): PendingCharge {
    return {
        key,
        signature: chargeSignatureOf(options, draft),
        options,
        draft,
        amountCents: chargeAmountOf(options, draft.kind),
        installmentNumbers: installmentNumbersOf(options, draft),
        createdAt: now,
    };
}

const pendingByCredit = new Map<string, PendingCharge>();

/** El cobro sin confirmar del crédito; el vencido se descarta. */
export function pendingChargeOf(creditId: string, now: number = Date.now()): PendingCharge | null {
    const pending = pendingByCredit.get(creditId);
    if (!pending) return null;
    if (now - pending.createdAt >= PENDING_CHARGE_TTL_MS) {
        pendingByCredit.delete(creditId);
        return null;
    }
    return pending;
}

export function savePendingCharge(creditId: string, pending: PendingCharge): void {
    pendingByCredit.set(creditId, pending);
}

export function dropPendingCharge(creditId: string): void {
    pendingByCredit.delete(creditId);
}

export function clearPendingCharges(): void {
    pendingByCredit.clear();
}

// Cerrar sesión o entrar con otro usuario borra los cobros del anterior.
useAuthStore.subscribe((state, previous) => {
    if (state.user?.cognitoSub !== previous.user?.cognitoSub) clearPendingCharges();
});
